import { createTheme } from '@mui/material/styles';

/**
 * Единая тема MUI для всей панели.
 *
 * ЗАЧЕМ: до неё приложение жило на дефолтной теме MUI, и каждая страница
 * решала вопросы плотности сама. Отсюда разнобой и проблемы на телефоне:
 * крупные заголовки съедали экран, диалоги не помещались, таблицы распирали
 * страницу. Тема задаёт общий, чуть более компактный масштаб и мобильные
 * правила там, где их можно применить централизованно.
 *
 * ВАЖНО: тема только уменьшает масштаб и плотность. Цвета, палитра и поведение
 * компонентов не переопределяются — страницы по-прежнему красят себя сами через
 * `themePalette` и `useAppTheme`, и ни одна из них не должна поменять логику.
 */

const theme = createTheme({
  typography: {
    /* База MUI — 14px. 13.5 даёт ровно «чуть мельче», не ломая существующие
       вёрстки: все rem-размеры внутри компонентов пересчитываются пропорционально. */
    fontSize: 13.5,
    h4: { fontSize: '1.75rem', '@media (max-width:600px)': { fontSize: '1.4rem' } },
    h5: { fontSize: '1.4rem', '@media (max-width:600px)': { fontSize: '1.15rem' } },
    h6: { fontSize: '1.1rem', '@media (max-width:600px)': { fontSize: '1rem' } },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    /* Кнопки перестают быть КАПСОМ: капс шире на ~15% и первым вылезает за край
       узкого экрана, плюс с ним подписи режутся. */
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none' },
      },
    },
    /* Диалоги на телефоне занимают почти весь экран — иначе содержимое
       (формы прав, модерация подарков, аналитика) не помещается в узкое окно. */
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width:600px)': {
            margin: 8,
            width: 'calc(100% - 16px)',
            maxWidth: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px)',
          },
        },
      },
    },
    /* Плотность таблиц на телефоне: колонки не убираем, но ужимаем поля,
       чтобы в горизонтальный скролл попадало больше данных. */
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '8px 8px',
            fontSize: '0.8rem',
          },
        },
        sizeSmall: {
          '@media (max-width:600px)': { padding: '6px 8px' },
        },
      },
    },
    /* Табы: на узком экране всегда со стрелками прокрутки, иначе последние
       вкладки становятся недоступны (ловили на настройках виджета). */
    MuiTabs: {
      defaultProps: { allowScrollButtonsMobile: true },
    },
    /* Панель постраничной навигации на узком экране не влезает в строку —
       разрешаем перенос, иначе она распирает таблицу и страницу. */
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          '@media (max-width:600px)': {
            flexWrap: 'wrap',
            rowGap: 4,
            paddingLeft: 8,
          },
        },
        spacer: {
          '@media (max-width:600px)': { flex: 'none' },
        },
      },
    },
    /* Иконка-кнопка на телефоне — не меньше 40px, иначе в неё не попасть пальцем. */
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          '@media (max-width:600px)': { padding: 8 },
        },
      },
    },
  },
});

export default theme;
